import { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateNote } from '../services/api';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions {
  noteId: number;
  delay?: number;
}

export function useAutoSave({ noteId, delay = 1500 }: UseAutoSaveOptions) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ title?: string; content?: string }>({});
  const lastSavedAtRef = useRef<number>(0);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { title?: string; content?: string }) =>
      updateNote(noteId, data),
    onSuccess: () => {
      setSaveStatus('saved');
      setIsDirty(false);
      lastSavedAtRef.current = Date.now();
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      // Reset to idle after showing "Saved" briefly
      setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
    },
    onError: () => {
      setSaveStatus('error');
    },
  });

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const data = pendingRef.current;
    if (data.title !== undefined || data.content !== undefined) {
      setSaveStatus('saving');
      mutation.mutate({ ...data });
      pendingRef.current = {};
    }
  }, [mutation]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, delay);
  }, [flush, delay]);

  const handleChange = useCallback(
    (content: string) => {
      pendingRef.current.content = content;
      setIsDirty(true);
      setSaveStatus('idle');
      scheduleSave();
    },
    [scheduleSave]
  );

  const handleTitleChange = useCallback(
    (title: string) => {
      pendingRef.current.title = title;
      setIsDirty(true);
      setSaveStatus('idle');
      scheduleSave();
    },
    [scheduleSave]
  );

  const saveNow = useCallback(() => {
    flush();
  }, [flush]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const data = pendingRef.current;
      if (data.title !== undefined || data.content !== undefined) {
        // Fire-and-forget save on unmount
        updateNote(noteId, data).catch(() => {});
      }
    };
  }, [noteId]);

  return { handleChange, handleTitleChange, saveNow, saveStatus, isDirty, lastSavedAtRef };
}
